import { useMemo, useRef, useState } from "react";
import type { FilmProject, ProjectScriptUpload, Scene } from "../types";
import { formatRuntime, sceneStatus } from "../utils/storyboard";
import { Icon } from "./Icon";

interface ScriptRailProps {
  project: FilmProject;
  selectedSceneId: string;
  scriptUpload?: ProjectScriptUpload;
  isAnalyzing: boolean;
  onSelectScene: (sceneId: string) => void;
  onScriptUpload: (file: File) => void;
  onAnalyze: () => void;
}

type RailTab = "script" | "breakdown";

function sceneMatchesSearch(scene: Scene, query: string): boolean {
  if (!query) return true;
  return [
    scene.slugline,
    scene.synopsis,
    scene.location,
    ...scene.characters
  ].some((value) => value.toLowerCase().includes(query));
}

export function ScriptRail({
  project,
  selectedSceneId,
  scriptUpload,
  isAnalyzing,
  onSelectScene,
  onScriptUpload,
  onAnalyze
}: ScriptRailProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<RailTab>("script");
  const [searchQuery, setSearchQuery] = useState("");
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredScenes = useMemo(
    () => project.scenes.filter((scene) => sceneMatchesSearch(scene, normalizedSearch)),
    [normalizedSearch, project.scenes]
  );
  const scriptMatches = !normalizedSearch || project.scriptExcerpt.toLowerCase().includes(normalizedSearch);

  return (
    <section className="script-rail" aria-label="Script and parsed scenes">
      <input
        ref={inputRef}
        className="visually-hidden"
        type="file"
        accept=".pdf,.txt,.text,.fountain,.fdx,.rtf,text/*,application/pdf"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onScriptUpload(file);
          event.target.value = "";
        }}
      />
      <header className="rail-tabs">
        <button className={activeTab === "script" ? "active" : ""} onClick={() => setActiveTab("script")} type="button">
          Script
        </button>
        <button className={activeTab === "breakdown" ? "active" : ""} onClick={() => setActiveTab("breakdown")} type="button">
          Breakdown
        </button>
      </header>

      <div className="rail-controls">
        <label>
          <span>Full Script</span>
          <select value="shooting-script" onChange={() => undefined}>
            <option value="shooting-script">Shooting Script</option>
            <option value="pitch-cut">Pitch Cut</option>
            <option value="storyboard-pass">Storyboard Pass</option>
          </select>
        </label>
        <input
          type="search"
          placeholder="Search script..."
          aria-label="Search script"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
        <div className="rail-upload-row">
          <button className="secondary-action" onClick={() => inputRef.current?.click()} type="button">
            <Icon name="upload" />
            Upload Script
          </button>
          <button className="primary-action" disabled={isAnalyzing} onClick={onAnalyze} type="button">
            <Icon name="spark" />
            {isAnalyzing ? "Analyzing" : "Analyze"}
          </button>
        </div>
        <p className="upload-note">
          {scriptUpload
            ? `${scriptUpload.fileName} · ${(scriptUpload.size / 1024).toFixed(0)} KB · ready for parser`
            : "Upload a script or analyze the current sample excerpt."}
        </p>
      </div>

      <div className="scene-list" aria-label="Parsed scenes">
        {filteredScenes.length ? filteredScenes.map((scene) => (
          <SceneButton
            key={scene.id}
            scene={scene}
            selected={scene.id === selectedSceneId}
            onClick={() => onSelectScene(scene.id)}
          />
        )) : (
          <RailEmptyState
            title="No scene matches"
            copy="Try a character, location, slugline, or clear the search."
            actionLabel="Clear search"
            onAction={() => setSearchQuery("")}
          />
        )}
      </div>

      {activeTab === "script" ? (
        scriptMatches ? (
          <pre className="script-excerpt">{project.scriptExcerpt}</pre>
        ) : (
          <RailEmptyState
            title="No script text matches"
            copy="The scene list may still contain metadata matches. Clear search to restore the full script."
            actionLabel="Clear search"
            onAction={() => setSearchQuery("")}
          />
        )
      ) : (
        <div className="script-breakdown-list" aria-label="Scene breakdown">
          {filteredScenes.length ? filteredScenes.map((scene) => (
            <BreakdownButton
              key={scene.id}
              scene={scene}
              selected={scene.id === selectedSceneId}
              onClick={() => onSelectScene(scene.id)}
            />
          )) : (
            <RailEmptyState
              title="No breakdown matches"
              copy="Try a character, location, slugline, or clear the search."
              actionLabel="Clear search"
              onAction={() => setSearchQuery("")}
            />
          )}
        </div>
      )}
    </section>
  );
}

function SceneButton({ scene, selected, onClick }: { scene: Scene; selected: boolean; onClick: () => void }) {
  return (
    <button className={`scene-button ${selected ? "selected" : ""}`} onClick={onClick} type="button">
      <span className="scene-number">{String(scene.number).padStart(2, "0")}</span>
      <span className="scene-button-copy">
        <strong>{scene.slugline}</strong>
        <small>{scene.synopsis}</small>
        <span className="scene-button-meta">
          <em className={`readiness ${scene.readiness}`}>{sceneStatus(scene)}</em>
          <span className="meta-separator" aria-hidden="true">{" · "}</span>
          <time>{formatRuntime(scene.durationSeconds)}</time>
        </span>
      </span>
    </button>
  );
}

function BreakdownButton({ scene, selected, onClick }: { scene: Scene; selected: boolean; onClick: () => void }) {
  return (
    <button className={`breakdown-card ${selected ? "selected" : ""}`} onClick={onClick} type="button">
      <span>Scene {String(scene.number).padStart(2, "0")}</span>
      <strong>{scene.slugline}</strong>
      <p>{scene.synopsis}</p>
      <dl>
        <div>
          <dt>Location</dt>
          <dd>{scene.location || "Unassigned"}</dd>
        </div>
        <div>
          <dt>Characters</dt>
          <dd>{scene.characters.length ? scene.characters.join(", ") : "No speaking roles tagged"}</dd>
        </div>
        <div>
          <dt>Timing</dt>
          <dd>{formatRuntime(scene.durationSeconds)}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>{sceneStatus(scene)}</dd>
        </div>
      </dl>
    </button>
  );
}

function RailEmptyState({ title, copy, actionLabel, onAction }: { title: string; copy: string; actionLabel: string; onAction: () => void }) {
  return (
    <section className="rail-empty-state">
      <Icon name="script" />
      <strong>{title}</strong>
      <p>{copy}</p>
      <button className="secondary-action compact-action" onClick={onAction} type="button">
        {actionLabel}
      </button>
    </section>
  );
}
